<template>
  <div class="services-page">
    <Banner title="企业服务商品" subtitle="查看入驻企业提供的各类服务与产品" />
    
    <div class="container">
      <!-- 分类筛选 -->
      <div class="category-filter">
        <el-button 
          :type="selectedCategory === '' ? 'primary' : ''" 
          @click="filterByCategory('')">
          全部
        </el-button>
        <el-button 
          v-for="cat in categories" 
          :key="cat"
          :type="selectedCategory === cat ? 'primary' : ''" 
          @click="filterByCategory(cat)">
          {{ cat }}
        </el-button>
      </div>

      <!-- 服务商品列表 -->
      <div class="services-grid">
        <el-card 
          v-for="item in filteredServices" 
          :key="item.product.id" 
          class="service-card"
          shadow="hover">
          <!-- 商品展览图 -->
          <div class="service-image" v-if="item.product.coverImage">
            <el-image 
              :src="$http.getFullImageUrl(item.product.coverImage)" 
              fit="cover" 
              class="cover-img"
              lazy>
              <div slot="placeholder" class="image-slot">
                <i class="el-icon-loading"></i>
              </div>
              <div slot="error" class="image-slot">
                <i class="el-icon-picture-outline"></i>
              </div>
            </el-image>
          </div>
          <div class="service-content">
            <div class="enterprise-info" v-if="item.enterprise">
              <el-avatar :size="40" :src="$http.getFullImageUrl(item.enterprise.logo)">
                {{ item.enterprise.name.charAt(0) }}
              </el-avatar>
              <span class="enterprise-name">{{ item.enterprise.name }}</span>
            </div>
            
            <div class="service-header">
              <h3>{{ item.product.name }}</h3>
              <el-tag>{{ item.product.category }}</el-tag>
            </div>
            
            <p class="service-description">{{ item.product.description }}</p>
            
            <div class="service-footer">
              <div class="price">
                <span class="amount">¥{{ item.product.price }}</span>
                <span class="unit">/{{ item.product.priceUnit }}</span>
              </div>
              <el-button type="primary" size="small" @click="viewDetail(item.product.id)">
                查看详情
              </el-button>
            </div>
          </div>
        </el-card>
      </div>

      <div v-if="filteredServices.length === 0" class="empty">
        <el-empty description="暂无服务商品"></el-empty>
      </div>
    </div>

    <!-- 详情对话框 -->
    <el-dialog 
      :visible.sync="detailDialogVisible" 
      :title="currentDetail.product ? currentDetail.product.name : ''"
      width="70%">
      <div v-if="currentDetail.product" class="service-detail">
        <el-row :gutter="20">
          <!-- 左侧：商品大图 -->
          <el-col :span="currentDetail.product.coverImage ? 10 : 0" v-if="currentDetail.product.coverImage">
            <div class="detail-image-section">
              <el-image 
                :src="$http.getFullImageUrl(currentDetail.product.coverImage)" 
                fit="cover" 
                class="detail-cover-img"
                :preview-src-list="[$http.getFullImageUrl(currentDetail.product.coverImage)]">
              </el-image>
            </div>
          </el-col>
          
          <!-- 右侧：详细内容 -->
          <el-col :span="currentDetail.product.coverImage ? 14 : 24">
            <!-- 企业信息 -->
            <div class="enterprise-section" v-if="currentDetail.enterprise">
              <h4>提供企业</h4>
              <div class="enterprise-card">
                <el-avatar :size="60" :src="$http.getFullImageUrl(currentDetail.enterprise.logo)">
                  {{ currentDetail.enterprise.name.charAt(0) }}
                </el-avatar>
                <div class="enterprise-info-detail">
                  <h3>{{ currentDetail.enterprise.name }}</h3>
                  <p>{{ currentDetail.enterprise.industry }} | {{ currentDetail.enterprise.scale }}</p>
                  <el-button size="small" @click="viewEnterprise(currentDetail.enterprise.id)">
                    查看企业详情
                  </el-button>
                </div>
              </div>
            </div>

            <!-- 服务信息 -->
            <el-descriptions :column="1" border class="detail-descriptions">
              <el-descriptions-item label="服务分类">{{ currentDetail.product.category }}</el-descriptions-item>
              <el-descriptions-item label="价格">
                ¥{{ currentDetail.product.price }} / {{ currentDetail.product.priceUnit }}
              </el-descriptions-item>
              <el-descriptions-item label="合作方式">
                {{ currentDetail.product.cooperationMode || '面议' }}
              </el-descriptions-item>
              <el-descriptions-item label="联系方式">
                {{ currentDetail.product.contactInfo || '请通过企业详情页联系' }}
              </el-descriptions-item>
            </el-descriptions>
            
            <div class="detail-section">
              <h4>服务描述</h4>
              <p>{{ currentDetail.product.description }}</p>
            </div>

            <div class="detail-section" v-if="currentDetail.product.features">
              <h4>服务特点</h4>
              <ul>
                <li v-for="(feature, index) in parseFeatures(currentDetail.product.features)" :key="index">
                  {{ feature }}
                </li>
              </ul>
            </div>
          </el-col>
        </el-row>
      </div>
      <span slot="footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button type="success" @click="handleBuy">立即购买</el-button>
        <el-button type="primary" @click="contactEnterprise">联系企业</el-button>
      </span>
    </el-dialog>

    <!-- 购买对话框（微信Native支付） -->
    <el-dialog
      title="购买服务"
      :visible.sync="purchaseDialogVisible"
      width="600px"
      append-to-body
      :close-on-click-modal="false"
      @close="handlePurchaseClose">
      <div v-if="currentDetail.product" class="purchase-container">
        <!-- 商品简要信息 -->
        <div class="product-summary">
          <h4>{{ currentDetail.product.name }}</h4>
          <div class="product-price">
            ¥{{ currentDetail.product.price }} <span class="unit">/ {{ currentDetail.product.priceUnit }}</span>
          </div>
        </div>

        <!-- 步骤一：填写信息 -->
        <div v-if="payStep === 1">
          <el-form :model="purchaseForm" :rules="purchaseRules" ref="purchaseForm" label-width="100px">
            <el-form-item label="您的姓名" prop="customerName">
              <el-input v-model="purchaseForm.customerName" placeholder="请输入您的姓名"></el-input>
            </el-form-item>
            <el-form-item label="联系电话" prop="customerPhone">
              <el-input v-model="purchaseForm.customerPhone" placeholder="请输入您的手机号"></el-input>
            </el-form-item>
            <el-form-item label="备注信息" prop="remark">
              <el-input type="textarea" v-model="purchaseForm.remark" placeholder="选填，如有特殊需求请备注"></el-input>
            </el-form-item>
          </el-form>
        </div>

        <!-- 步骤二：扫码支付 -->
        <div v-if="payStep === 2" class="qrcode-section">
          <div class="pay-status-info">
            <i class="el-icon-time" style="color: #E6A23C; font-size: 18px;"></i>
            <span>请使用微信扫码支付</span>
          </div>
          <div class="qrcode-wrapper">
            <canvas ref="qrcodeCanvas" class="qrcode-canvas"></canvas>
          </div>
          <div class="pay-amount">
            支付金额：<span class="highlight">¥{{ payOrderInfo.amount }}</span>
          </div>
          <div class="pay-tip">
            <p>打开微信 → 扫一扫 → 扫描上方二维码完成支付</p>
            <p class="sub-tip">支付完成后将自动跳转，请勿关闭此窗口</p>
          </div>
          <div class="polling-indicator" v-if="pollingStatus">
            <i class="el-icon-loading"></i> 正在等待支付结果...
          </div>
        </div>

        <!-- 步骤三：支付成功 -->
        <div v-if="payStep === 3" class="pay-success">
          <i class="el-icon-success" style="font-size: 64px; color: #67C23A;"></i>
          <h3>支付成功！</h3>
          <p>订单编号：{{ payOrderInfo.orderId }}</p>
          <p>微信交易号：{{ payResult.transactionId }}</p>
        </div>
      </div>
      <span slot="footer">
        <template v-if="payStep === 1">
          <el-button @click="purchaseDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitPurchase" :loading="submittingPurchase" :disabled="submittingPurchase">
            确认并支付
          </el-button>
        </template>
        <template v-if="payStep === 2">
          <el-button @click="cancelPay">取消支付</el-button>
        </template>
        <template v-if="payStep === 3">
          <el-button type="primary" @click="finishPay">完成</el-button>
        </template>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Banner from '../components/Banner.vue';
import api from '../api/config';

export default {
  name: 'Services',
  components: {
    Banner
  },
  data() {
    return {
      services: [],
      selectedCategory: '',
      categories: [],
      detailDialogVisible: false,
      currentDetail: {},
      // 购买相关
      purchaseDialogVisible: false,
      submittingPurchase: false,
      payStep: 1, // 1=填写信息, 2=扫码支付, 3=支付成功
      purchaseForm: {
        customerName: '',
        customerPhone: '',
        remark: ''
      },
      purchaseRules: {
        customerName: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        customerPhone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ]
      },
      // 支付状态
      payOrderInfo: {},   // { orderId, outTradeNo, codeUrl, amount }
      payResult: {},      // { transactionId, payTime }
      pollingStatus: false,
      pollingTimer: null
    };
  },
  computed: {
    filteredServices() {
      if (!this.selectedCategory) {
        return this.services;
      }
      return this.services.filter(item => item.product.category === this.selectedCategory);
    }
  },
  mounted() {
    this.loadServices();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async loadServices() {
      try {
        const response = await api.get('/services');
        this.services = response.data || [];
        
        // 提取分类
        const catSet = new Set(this.services.map(item => item.product.category));
        this.categories = Array.from(catSet);
      } catch (error) {
        this.$message.error('加载服务商品失败');
        console.error('加载服务商品失败:', error);
      }
    },
    filterByCategory(category) {
      this.selectedCategory = category;
    },
    async viewDetail(id) {
      try {
        const response = await api.get(`/services/${id}`);
        this.currentDetail = response.data || {};
        this.detailDialogVisible = true;
      } catch (error) {
        this.$message.error('加载详情失败');
        console.error('加载详情失败:', error);
      }
    },
    viewEnterprise(id) {
      this.detailDialogVisible = false;
      this.$router.push(`/enterprises/${id}`);
    },
    parseFeatures(features) {
      try {
        return JSON.parse(features);
      } catch {
        return [];
      }
    },
    contactEnterprise() {
      if (this.currentDetail.product && this.currentDetail.product.contactInfo) {
        this.$message.success('联系方式：' + this.currentDetail.product.contactInfo);
      } else {
        this.$message.info('请查看企业详情页获取联系方式');
      }
    },
    // ==================== 购买流程 ====================
    handleBuy() {
      this.payStep = 1;
      this.payOrderInfo = {};
      this.payResult = {};
      this.purchaseDialogVisible = true;
      this.resetPurchaseForm();
    },
    resetPurchaseForm() {
      this.purchaseForm = {
        customerName: '',
        customerPhone: '',
        remark: ''
      };
      if (this.$refs.purchaseForm) {
        this.$refs.purchaseForm.resetFields();
      }
    },
    // 提交订单 → 获取支付二维码
    submitPurchase() {
      this.$refs.purchaseForm.validate(async valid => {
        if (valid) {
          try {
            this.submittingPurchase = true;
            
            const response = await api.post('/public/wxpay/native-pay', {
              serviceId: this.currentDetail.product.id,
              customerName: this.purchaseForm.customerName,
              customerPhone: this.purchaseForm.customerPhone,
              remark: this.purchaseForm.remark
            });
            
            if (response.success) {
              this.payOrderInfo = response.data;
              this.payStep = 2;
              
              // 等待 DOM 更新后再绘制二维码
              this.$nextTick(() => {
                this.renderQRCode(response.data.codeUrl);
                this.startPolling(response.data.orderId);
              });
            } else {
              this.$message.error('下单失败: ' + response.message);
            }
          } catch (error) {
            console.error(error);
            this.$message.error('下单失败，请稍后重试');
          } finally {
            this.submittingPurchase = false;
          }
        }
      });
    },
    // 使用 Canvas 绘制二维码（纯前端，无需额外依赖）
    renderQRCode(codeUrl) {
      const canvas = this.$refs.qrcodeCanvas;
      if (!canvas) return;
      
      // 使用简易方式：通过 img 标签加载第三方QR Code API
      // 生产环境建议使用 npm 包 qrcodejs2
      const size = 240;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      
      // 通过 QR Code 在线 API 生成（备选方案）
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(codeUrl)}`;
      img.onload = () => {
        ctx.drawImage(img, 0, 0, size, size);
      };
      img.onerror = () => {
        // API 不可用时，显示文字提示
        ctx.fillStyle = '#f5f5f5';
        ctx.fillRect(0, 0, size, size);
        ctx.fillStyle = '#333';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('二维码加载失败', size/2, size/2 - 10);
        ctx.fillText('请刷新重试', size/2, size/2 + 10);
      };
    },
    // 轮询查询支付结果
    startPolling(orderId) {
      this.pollingStatus = true;
      let pollCount = 0;
      const maxPolls = 120; // 最多轮询120次（约2分钟）
      
      this.pollingTimer = setInterval(async () => {
        pollCount++;
        if (pollCount > maxPolls) {
          this.stopPolling();
          this.$message.warning('支付超时，请重新下单');
          this.payStep = 1;
          return;
        }
        
        try {
          const response = await api.get(`/public/wxpay/query/${orderId}`);
          if (response.success && response.data) {
            if (response.data.status === 'PAID') {
              this.stopPolling();
              this.payResult = response.data;
              this.payStep = 3;
              this.$message.success('支付成功！');
            } else if (response.data.status === 'CANCELLED') {
              this.stopPolling();
              this.$message.warning('订单已取消');
              this.purchaseDialogVisible = false;
            }
          }
        } catch (error) {
          console.error('查询支付状态失败:', error);
        }
      }, 2000); // 每2秒查询一次
    },
    stopPolling() {
      this.pollingStatus = false;
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    cancelPay() {
      this.stopPolling();
      this.$confirm('确定取消支付吗？', '提示', { type: 'warning' }).then(() => {
        this.purchaseDialogVisible = false;
      }).catch(() => {
        // 用户取消了确认，恢复轮询
        if (this.payStep === 2 && this.payOrderInfo.orderId) {
          this.startPolling(this.payOrderInfo.orderId);
        }
      });
    },
    finishPay() {
      this.purchaseDialogVisible = false;
      this.detailDialogVisible = false;
    },
    handlePurchaseClose() {
      this.stopPolling();
      this.payStep = 1;
    }
  }
};
</script>

<style lang="scss" scoped>
.services-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.container {
  max-width: 1240px;
  margin: 0 auto;
  padding: 40px 20px;
}

.category-filter {
  margin-bottom: 30px;
  text-align: center;
  
  .el-button {
    margin: 0 10px;
  }
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.service-card {
  transition: transform 0.3s;
  border-radius: 12px;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
  }

  ::v-deep .el-card__body {
    padding: 0;
  }
}

.service-image {
  height: 200px;
  overflow: hidden;
  position: relative;
  background: #f0f2f5;

  .cover-img {
    width: 100%;
    height: 100%;
    transition: transform 0.5s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  .image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: #f5f7fa;
    color: #909399;
    font-size: 24px;
  }
}

.service-content {
  .enterprise-info {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eee;
    
    .enterprise-name {
      margin-left: 10px;
      font-size: 14px;
      color: #666;
    }
  }
  
  .service-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    
    h3 {
      margin: 0;
      font-size: 18px;
      color: #333;
    }
  }
  
  .service-description {
    color: #666;
    line-height: 1.6;
    margin-bottom: 20px;
    min-height: 60px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .service-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .price {
      .amount {
        font-size: 24px;
        color: #f56c6c;
        font-weight: bold;
      }
      
      .unit {
        font-size: 14px;
        color: #999;
      }
    }
  }
}

.empty {
  text-align: center;
  padding: 60px 0;
}

.service-detail {
  .enterprise-section {
    margin-bottom: 30px;
    
    h4 {
      margin-bottom: 15px;
      color: #333;
      font-size: 16px;
    }
    
    .enterprise-card {
      display: flex;
      align-items: center;
      padding: 20px;
      background: #f9f9f9;
      border-radius: 8px;
      
      .enterprise-info-detail {
        margin-left: 20px;
        flex: 1;
        
        h3 {
          margin: 0 0 8px 0;
          font-size: 18px;
          color: #333;
        }
        
        p {
          margin: 0 0 10px 0;
          color: #666;
          font-size: 14px;
        }
      }
    }
  }
  
  .detail-descriptions {
    margin: 20px 0;
  }
  
  .detail-section {
    margin-top: 20px;
    
    h4 {
      margin-bottom: 10px;
      color: #333;
      font-size: 16px;
    }
    
    p {
      line-height: 1.8;
      color: #666;
    }
    
    ul {
      list-style: none;
      padding: 0;
      
      li {
        padding: 8px 0;
        padding-left: 20px;
        position: relative;
        
        &:before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #67c23a;
          font-weight: bold;
        }
      }
    }
  }
}

/* ===================== 购买对话框样式 ===================== */
.purchase-container {
  .product-summary {
    margin-bottom: 20px;
    padding: 15px;
    background: linear-gradient(135deg, #f0f9eb 0%, #e1f3d8 100%);
    border-radius: 8px;
    
    h4 {
      margin: 0 0 10px 0;
      color: #333;
    }
    
    .product-price {
      color: #f56c6c;
      font-weight: bold;
      font-size: 22px;
      
      .unit {
        font-size: 13px;
        color: #999;
        font-weight: normal;
      }
    }
  }

  .qrcode-section {
    text-align: center;
    
    .pay-status-info {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-bottom: 20px;
      font-size: 16px;
      color: #333;
      font-weight: 500;
    }
    
    .qrcode-wrapper {
      display: inline-block;
      padding: 16px;
      background: #fff;
      border: 2px solid #e6e6e6;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      
      .qrcode-canvas {
        display: block;
        width: 240px;
        height: 240px;
      }
    }
    
    .pay-amount {
      margin-top: 16px;
      font-size: 16px;
      color: #666;
      
      .highlight {
        color: #f56c6c;
        font-size: 24px;
        font-weight: bold;
      }
    }
    
    .pay-tip {
      margin-top: 16px;
      color: #999;
      font-size: 13px;
      line-height: 1.8;
      
      .sub-tip {
        color: #E6A23C;
        font-weight: 500;
      }
    }
    
    .polling-indicator {
      margin-top: 16px;
      color: #409EFF;
      font-size: 14px;
      animation: pulse 1.5s ease-in-out infinite;
    }
  }

  .pay-success {
    text-align: center;
    padding: 30px 0;
    
    h3 {
      margin: 16px 0 10px;
      color: #333;
      font-size: 20px;
    }
    
    p {
      color: #666;
      font-size: 14px;
      margin: 6px 0;
    }
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.detail-image-section {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}
.detail-cover-img {
  width: 100%;
  height: 350px;
  display: block;
  cursor: zoom-in;
  transition: transform 0.3s;
}
.detail-cover-img:hover {
  transform: scale(1.02);
}
</style>
